import React, { Suspense, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import * as Sentry from '@sentry/react';
import AppMain from './AppMain';
import { startFromSavedProfile } from '../store/actions/authActions/auth';
import { useResizeDetector } from 'react-resize-detector';
import { useAppSelector } from '../store/hooks';
import { matchPath, useLocation, useHistory } from 'react-router-dom';
import { setupBackButton } from '../capacitor/backButton';
import { initPushNotifications } from '../capacitor/pushNotifications';
import { markLiveUpdateReady } from '../capacitor/liveUpdate';
import { useNativeUpdateRequired } from '../capacitor/nativeBuild';
import { NativeUpdateRequired } from './platform/layout/NativeUpdateRequired';
import { useResponsive } from './platform/dashboards/components/hooks/useResponsive';
import { useMobileNav } from './platform/layout/mobileNav/useMobileNav';

// C-cockpit Task 1 — the flag-gated lazy cockpit route (adjudication (2)). Two things make the whole
// cockpit chunk dead-code-eliminated from a flag-off production build:
//
//  1. `REACT_APP_COCKPIT` is DECLARED (`.env`, default `false`), so react-scripts' DefinePlugin has a
//     DEFINED value to inline. An UNSET var inlines as an absent-key access on the injected
//     `process.env` object literal, which terser does NOT fold — the chunk would survive. The default
//     makes `process.env.REACT_APP_COCKPIT === 'true'` a foldable `"false" === "true"`.
//  2. The gate is an `if`-STATEMENT dead branch, not a `?:` initializer. webpack prunes a statically
//     false `if (…) { … import() … }` branch at parse time — removing the `import()` from the module
//     graph so the split chunk is never emitted. A `const x = cond ? React.lazy(()=>import()) : null`
//     ternary only gets its CALL removed by terser LATER, leaving webpack's already-split ~40KB chunk
//     as an orphan file in build/static/js (verified: the ternary form shipped the chunk; this form
//     does not).
//
// The literal comparison (not a cross-module `isCockpitEnabled(...)` call, which webpack can't prove
// constant) is required for the fold; `isCockpitEnabled` remains for the "flag off ⇒ nothing mounts"
// unit test. VERIFIED EMPIRICALLY (C-cockpit T1 review, 2026-07-21): a probe `npm run build` with the
// flag defaulted false emitted ZERO chunks containing `Capability Workbench` / `Coverage matrix` /
// `cockpit.paneManager` (grep of build/static/js). Enable in dev with `REACT_APP_COCKPIT=true npm start`.
// `typeof import(...)` is a TYPE-ONLY reference (erased by tsc — it creates NO webpack dependency, so
// the only runtime `import()` is the guarded one below).
let CockpitApp: React.LazyExoticComponent<(typeof import('../chat/cockpit/CockpitRoute'))['default']> | null = null;
if (process.env.REACT_APP_COCKPIT === 'true') {
    CockpitApp = React.lazy(() => import('../chat/cockpit/CockpitRoute'));
}

const Main: React.FC = () => {
    const dispatch = useDispatch();

    const colorScheme = useAppSelector((state) => state.ThemeOptions.colorScheme);
    const enableFixedHeader = useAppSelector((state) => state.ThemeOptions.enableFixedHeader);
    const enableFixedSidebar = useAppSelector((state) => state.ThemeOptions.enableFixedSidebar);
    const enableFixedFooter = useAppSelector((state) => state.ThemeOptions.enableFixedFooter);
    const enableClosedSidebar = useAppSelector((state) => state.ThemeOptions.enableClosedSidebar);
    const enableMobileMenu = useAppSelector((state) => state.ThemeOptions.enableMobileMenu);
    const enablePageTabsAlt = useAppSelector((state) => state.ThemeOptions.enablePageTabsAlt);

    const location = useLocation();
    const history = useHistory();
    const { isDesktop } = useResponsive();
    const mobileNav = useMobileNav();
    const nativeUpdateRequired = useNativeUpdateRequired();

    const match: any = matchPath(location.pathname, {
        path: '/dashboard/:module/:nodeId',
        exact: false,
    });
    const currentModule = match?.params.module;

    useEffect(() => {
        dispatch(startFromSavedProfile());
        markLiveUpdateReady();
        setupBackButton(history);
        initPushNotifications(history);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time mount setup (saved profile, live update, back button, push notifications); dispatch/history stable
    }, []);

    const { ref: resizeRef } = useResizeDetector({ refreshMode: 'debounce', refreshRate: 0 });

    if (nativeUpdateRequired) return <NativeUpdateRequired />;

    // C-cockpit Task 1: the cockpit mounts full-screen, BEFORE the app shell (no sidebar/header, no
    // auth gate — panes carry explicit cast tokens, never the app session). Placed after every hook so
    // the early return never changes hook order; `CockpitApp` is null (and this whole branch dead) in
    // an unset-flag build.
    // exact: matchPath is prefix-matching by default, and '/cockpit-old' (or any
    // '/cockpit*' path) must not be swallowed by the flag-gated dev route.
    if (CockpitApp !== null && matchPath(location.pathname, { path: '/cockpit', exact: true })) {
        return (
            <Suspense fallback={<div>Loading cockpit…</div>}>
                <CockpitApp />
            </Suspense>
        );
    }

    return (
        <div
            ref={resizeRef}
            className={cx(
                'app-container',
                `app-theme-${colorScheme}`,
                { 'fixed-header': enableFixedHeader },
                { 'fixed-sidebar': enableFixedSidebar || !isDesktop },
                { 'fixed-footer': enableFixedFooter },
                { 'closed-sidebar': enableClosedSidebar || !isDesktop },
                { 'sidebar-hover-enabled': currentModule !== 'analysis' },
                { 'closed-sidebar-mobile': !isDesktop },
                { 'sidebar-mobile-open': enableMobileMenu },
                { 'body-tabs-shadow-btn': enablePageTabsAlt },
                { 'mobile-nav': mobileNav },
            )}
        >
            <AppMain />
        </div>
    );
};

export default Sentry.withProfiler(Main);
